/**
 * Model prices and the lookup that resolves them — the half of pricing that has
 * no I/O.
 *
 * Split out of `pricing.ts` because that module reads a cache file, which makes
 * it Node-only, which made *cost calculation* Node-only by association. The
 * browser has to price the same tokens the same way once the dashboard reads
 * from the dead drop, and two implementations of a pricing rule would drift
 * silently: the same history would be worth different amounts depending on which
 * machine you looked at it from.
 *
 * Nothing here touches the filesystem or the network. `pricing.ts` supplies the
 * table; this decides what a model costs.
 */

/** Per-token prices in USD. Mirrors the relevant LiteLLM fields. */
export interface ModelPrice {
  input: number;
  output: number;
  /** Cost per token to read from prompt cache. */
  cacheRead: number;
  /** Cost per token to write the 5-minute-TTL cache (~1.25x input). */
  cacheWrite: number;
  /** Cost per token to write the 1-hour-TTL cache (~2x input). */
  cacheWrite1h: number;
}

export type PriceTable = Record<string, ModelPrice>;

/** Resolves a model id to its price, or null when the model is unknown. */
export type PriceLookup = (model: string | null) => ModelPrice | null;

/**
 * Built-in fallback rates (USD per token) so cost estimation works offline and
 * before the first LiteLLM refresh. Refreshed values and user overrides take
 * precedence. Keys are stored normalized (see {@link normalize}). Values are the
 * standard published tiers as of 2026.
 */
export const FALLBACK: PriceTable = {
  // Anthropic — priced per the Haiku/Sonnet/Opus tiers (1h cache = 2x input).
  "claude-haiku-4-5": { input: 1e-6, output: 5e-6, cacheWrite: 1.25e-6, cacheWrite1h: 2e-6, cacheRead: 1e-7 },
  "claude-sonnet-4-5": { input: 3e-6, output: 15e-6, cacheWrite: 3.75e-6, cacheWrite1h: 6e-6, cacheRead: 3e-7 },
  "claude-sonnet-4-6": { input: 3e-6, output: 15e-6, cacheWrite: 3.75e-6, cacheWrite1h: 6e-6, cacheRead: 3e-7 },
  "claude-sonnet-5": { input: 3e-6, output: 15e-6, cacheWrite: 3.75e-6, cacheWrite1h: 6e-6, cacheRead: 3e-7 },
  // Opus 4.5+ dropped to the $5/$25 tier (was $15/$75 on 4.0/4.1).
  "claude-opus-4": { input: 5e-6, output: 25e-6, cacheWrite: 6.25e-6, cacheWrite1h: 10e-6, cacheRead: 5e-7 },
  "claude-fable-5": { input: 10e-6, output: 50e-6, cacheWrite: 12.5e-6, cacheWrite1h: 20e-6, cacheRead: 1e-6 },
  // OpenAI Codex (gpt-5 family) — generic fallback (no 1h cache tier).
  "gpt-5-codex": { input: 1.25e-6, output: 10e-6, cacheWrite: 1.25e-6, cacheWrite1h: 1.25e-6, cacheRead: 1.25e-7 },
  "gpt-5": { input: 1.25e-6, output: 10e-6, cacheWrite: 1.25e-6, cacheWrite1h: 1.25e-6, cacheRead: 1.25e-7 },
  // Google Gemini (Antigravity) — Gemini Pro tier fallback.
  "gemini-3-pro": { input: 1.25e-6, output: 10e-6, cacheWrite: 1.25e-6, cacheWrite1h: 1.25e-6, cacheRead: 3.125e-7 },
};

/** Normalize a model id to a stable key: strip provider prefixes, dates, versions. */
export function normalize(model: string): string {
  let m = model.toLowerCase().trim();
  m = m.replace(/^[a-z0-9_-]+\//, ""); // slash vendor prefix: "anthropic/..."
  m = m.replace(/^(us|eu|apac|global)\./, ""); // region prefix: "us.anthropic..."
  m = m.replace(/^(anthropic|openai|google|bedrock|vertex)\./, ""); // dotted vendor
  m = m.replace(/[-@]\d{4}-?\d{2}-?\d{2}$/, ""); // dashed/at date: "-2025-10-01"
  m = m.replace(/-\d{8}$/, ""); // compact date: "-20251001"
  m = m.replace(/-v\d+:\d+$/, ""); // bedrock version: "-v1:0"
  m = m.replace(/\[\d+[km]\]$/, ""); // context marker: "[1m]"
  m = m.replace(/-(latest|fast)$/, ""); // variant aliases
  return m.trim();
}

/**
 * Build a memoized price lookup over a table.
 *
 * The memo lives inside the closure rather than at module scope, so a caller
 * holding two tables (built-ins vs. a freshly fetched feed) can't have one
 * poison the other's cache — which is exactly what a shared module-level memo
 * did before this was extracted.
 *
 * Resolution tolerates date suffixes and provider prefixes (e.g.
 * "claude-haiku-4-5-20251001" -> "claude-haiku-4-5",
 * "us.anthropic.claude-opus-4-5-v1:0" -> "claude-opus-4-5").
 */
export function makePriceLookup(table: PriceTable): PriceLookup {
  const memo = new Map<string, ModelPrice | null>();

  return (model: string | null): ModelPrice | null => {
    if (!model) return null;
    const key = normalize(model);
    const hit = memo.get(key);
    if (hit !== undefined) return hit;

    let resolved: ModelPrice | null = table[key] ?? null;
    if (!resolved) {
      // Longest-prefix match: pick the most specific known model the id starts
      // with. Bare family keys without a hyphen (LiteLLM has a legacy "claude"
      // entry) are excluded — they'd swallow every new model at the wrong rate.
      let bestLen = 0;
      for (const [name, price] of Object.entries(table)) {
        if (!name.includes("-")) continue;
        if (key.startsWith(name) && name.length > bestLen) {
          resolved = price;
          bestLen = name.length;
        }
      }
    }

    memo.set(key, resolved);
    return resolved;
  };
}
